import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Shield, Plus, Trash2, Loader2, LogOut, Building2, DollarSign,
  Pencil, Power, PowerOff, Search, BarChart3,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  getFinanceToken, getFinanceUser, clearFinanceSession,
} from './FinanceAuthPage';
import FinanceTenantAnalytics from './FinanceTenantAnalytics';

function financeHeaders() {
  const t = getFinanceToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function MethodForm({ initial, tenants, onSubmit, onCancel, saving }) {
  const [tenantId, setTenantId] = useState(initial?.tenant_id || '');
  const [name, setName] = useState(initial?.name || '');
  const [method, setMethod] = useState(initial?.method || '');
  const [flow, setFlow] = useState(initial?.flow || 'both');
  const [visibility, setVisibility] = useState(initial?.visibility || 'both');
  const [instructions, setInstructions] = useState(initial?.method_details || initial?.instructions || '');
  const [receivingAddress, setReceivingAddress] = useState(initial?.receiving_address || '');
  const [qrCodeUrl, setQrCodeUrl] = useState(initial?.qr_code_url || '');
  const [minAmount, setMinAmount] = useState(initial?.min_amount ?? 0);
  const [maxAmount, setMaxAmount] = useState(initial?.max_amount ?? 0);
  const [allowedAmounts, setAllowedAmounts] = useState(
    (initial?.preset_amounts || initial?.allowed_amounts || []).join(', ')
  );
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);

  const submit = () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    const payload = {
      tenant_id: tenantId || null,
      name: name.trim(),
      method: method.trim() || undefined,
      flow,
      visibility,
      instructions,
      method_details: instructions,
      receiving_address: receivingAddress.trim(),
      qr_code_url: qrCodeUrl.trim(),
      min_amount: Number(minAmount) || 0,
      max_amount: Number(maxAmount) || 0,
      allowed_amounts: allowedAmounts
        .split(',').map((s) => Number(s.trim())).filter((n) => !Number.isNaN(n) && n > 0),
      enabled,
    };
    onSubmit(payload);
  };

  return (
    <div className="rounded-2xl border border-blue-500/30 bg-blue-500/5 p-5 space-y-4" data-testid="method-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Tenant</label>
          <select
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-white"
            data-testid="method-tenant"
            disabled={!!initial}
          >
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.is_main ? '★ ' : ''}{t.name} {t.slug ? `(/${t.slug})` : ''}
              </option>
            ))}
          </select>
          {initial && <div className="text-xs text-gray-500 mt-1">Tenant cannot be changed on edit.</div>}
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Display Name *</label>
          <Input
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. CashApp - Chime Card"
            className="bg-[#0B0E14] border-white/10 text-white"
            data-testid="method-name"
          />
          <div className="text-xs text-gray-500 mt-1">
            Include <b>"chime"</b> in the name to route deposits to the Chime In Bot.
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Slug / Code</label>
          <Input
            value={method} onChange={(e) => setMethod(e.target.value)}
            placeholder="auto from name if empty"
            className="bg-[#0B0E14] border-white/10 text-white"
            data-testid="method-slug"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Player Flow</label>
            <select
              value={flow}
              onChange={(e) => setFlow(e.target.value)}
              className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-white"
              data-testid="method-flow"
            >
              <option value="both">Deposit + Withdraw</option>
              <option value="deposit">Deposit only</option>
              <option value="withdraw">Withdraw only</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Visibility</label>
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-white"
              data-testid="method-visibility"
            >
              <option value="both">Player app + admin</option>
              <option value="app">Player app only</option>
              <option value="admin">Admin only</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Min Amount</label>
            <Input type="number" value={minAmount} onChange={(e) => setMinAmount(e.target.value)}
              className="bg-[#0B0E14] border-white/10 text-white" data-testid="method-min" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Max Amount</label>
            <Input type="number" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)}
              className="bg-[#0B0E14] border-white/10 text-white" data-testid="method-max" />
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-400 mb-1">Allowed Amounts (comma-separated)</label>
          <Input value={allowedAmounts} onChange={(e) => setAllowedAmounts(e.target.value)}
            placeholder="25, 50, 100, 250, 500"
            className="bg-[#0B0E14] border-white/10 text-white" data-testid="method-allowed" />
        </div>
        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Receiving Tag / Address</label>
            <Input
              value={receivingAddress}
              onChange={(e) => setReceivingAddress(e.target.value)}
              placeholder="$cashtag, Chime tag, wallet address, phone, email"
              className="bg-[#0B0E14] border-white/10 text-white"
              data-testid="method-receiving-address"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">QR / Payment Image URL</label>
            <Input
              value={qrCodeUrl}
              onChange={(e) => setQrCodeUrl(e.target.value)}
              placeholder="https://..."
              className="bg-[#0B0E14] border-white/10 text-white"
              data-testid="method-qr-url"
            />
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-400 mb-1">Instructions (shown to player)</label>
          <Textarea rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)}
            className="bg-[#0B0E14] border-white/10 text-white" data-testid="method-instructions" />
          <div className="text-xs text-gray-500 mt-1">
            Manual methods are created as <b>LOCAL_MANUAL</b>, so the same method appears in player deposit/withdraw flows.
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-white/5">
        <label className="inline-flex items-center gap-2 text-sm text-gray-300">
          <Switch checked={!!enabled} onCheckedChange={setEnabled} data-testid="method-enabled" />
          Enabled (visible to players)
        </label>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="method-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="method-save"
            style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff' }}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {initial ? 'Save Changes' : 'Create Method'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function FinanceDashboard() {
  const navigate = useNavigate();
  const user = getFinanceUser();
  const [activeTab, setActiveTab] = useState('methods'); // 'methods' | 'analytics'
  const [tenants, setTenants] = useState([]);
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTenant, setFilterTenant] = useState('');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getFinanceToken()) {
      navigate('/finance/auth', { replace: true });
    }
  }, [navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [t, m] = await Promise.all([
        api.get('/api/finance/tenants', { headers: financeHeaders() }),
        api.get('/api/finance/payment-methods', { headers: financeHeaders() }),
      ]);
      setTenants(t.data?.tenants || []);
      setMethods(m.data?.methods || []);
    } catch (e) {
      if (e.response?.status === 401) {
        clearFinanceSession();
        navigate('/finance/auth', { replace: true });
        return;
      }
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const tenantById = useMemo(() => {
    const m = {};
    tenants.forEach((t) => { m[t.id || ''] = t; });
    return m;
  }, [tenants]);

  const filtered = useMemo(() => {
    let rows = methods;
    if (filterTenant !== '') {
      const tid = filterTenant === 'main' ? '' : filterTenant;
      rows = rows.filter((m) => (m.tenant_id || '') === tid);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((m) =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.method || '').toLowerCase().includes(q) ||
        (m.instructions || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [methods, filterTenant, search]);

  const byTenant = useMemo(() => {
    const g = {};
    filtered.forEach((m) => {
      const key = m.tenant_id || '';
      (g[key] = g[key] || []).push(m);
    });
    return g;
  }, [filtered]);

  const logout = async () => {
    try { await api.post('/api/finance/auth/logout', {}, { headers: financeHeaders() }); } catch {}
    clearFinanceSession();
    navigate('/finance/auth', { replace: true });
  };

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        const { tenant_id: _tid, ...update } = payload;
        await api.put(`/api/finance/payment-methods/${editing.id}`, update, { headers: financeHeaders() });
        toast.success('Method updated');
      } else {
        await api.post('/api/finance/payment-methods', payload, { headers: financeHeaders() });
        toast.success('Method created');
      }
      setShowForm(false); setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const toggle = async (m) => {
    try {
      await api.post(`/api/finance/payment-methods/${m.id}/toggle`, {}, { headers: financeHeaders() });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Toggle failed'); }
  };

  const remove = async (m) => {
    if (!confirm(`Delete "${m.name}"? Players in this tenant won't see it anymore.`)) return;
    try {
      await api.delete(`/api/finance/payment-methods/${m.id}`, { headers: financeHeaders() });
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="min-h-screen" style={{ background: '#0B0E14' }} data-testid="finance-dashboard">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-white/10 bg-[#0B0E14]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-500/20 text-blue-400">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <div className="text-white font-bold">Finance Portal</div>
              <div className="text-xs text-gray-400">{user?.name || user?.email}</div>
            </div>
          </div>
          <Button variant="outline" onClick={logout} data-testid="finance-logout">
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-5">
        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-white/10 pb-3">
          <button
            onClick={() => setActiveTab('methods')}
            data-testid="finance-tab-methods"
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition border ${
              activeTab === 'methods'
                ? 'bg-blue-500/20 border-blue-500 text-blue-300'
                : 'bg-transparent border-white/10 text-gray-400 hover:text-gray-200'
            }`}
          >
            <DollarSign className="w-4 h-4" /> Payment Methods
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            data-testid="finance-tab-analytics"
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition border ${
              activeTab === 'analytics'
                ? 'bg-blue-500/20 border-blue-500 text-blue-300'
                : 'bg-transparent border-white/10 text-gray-400 hover:text-gray-200'
            }`}
          >
            <BarChart3 className="w-4 h-4" /> Tenant Analytics
          </button>
        </div>

        {activeTab === 'analytics' ? (
          <FinanceTenantAnalytics />
        ) : (
        <>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white">Manual Payment Methods</h1>
            <p className="text-sm text-gray-400">
              Manage all <b>manual</b> deposit/withdrawal methods across every tenant from one place.
            </p>
          </div>
          <Button
            onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="method-add"
            style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff' }}
          >
            <Plus className="w-4 h-4 mr-2" /> Add Method
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, slug or instructions…"
              className="bg-[#131822] border-white/10 text-white pl-9 w-72"
              data-testid="method-search"
            />
          </div>
          <select
            value={filterTenant} onChange={(e) => setFilterTenant(e.target.value)}
            className="bg-[#131822] border border-white/10 rounded-md px-3 py-2 text-white text-sm"
            data-testid="method-filter-tenant"
          >
            <option value="">All tenants</option>
            {tenants.map((t) => (
              <option key={t.id || 'main'} value={t.id === '' ? 'main' : t.id}>
                {t.is_main ? '★ ' : ''}{t.name}
              </option>
            ))}
          </select>
          <div className="text-xs text-gray-500">{filtered.length} result{filtered.length === 1 ? '' : 's'}</div>
        </div>

        {/* Form */}
        {showForm && (
          <MethodForm
            initial={editing}
            tenants={tenants}
            saving={saving}
            onSubmit={handleCreate}
            onCancel={() => { setShowForm(false); setEditing(null); }}
          />
        )}

        {/* List grouped by tenant */}
        {loading ? (
          <div className="text-gray-400 flex items-center gap-2 p-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-gray-500 py-16">
            No manual payment methods found. Use <b>Add Method</b> to create one.
          </div>
        ) : (
          <div className="space-y-6">
            {Object.keys(byTenant).map((tid) => {
              const t = tenantById[tid] || { name: 'Unknown', slug: '', is_main: false };
              return (
                <div key={tid || 'main'} className="rounded-2xl border border-white/10 bg-[#131822]" data-testid={`tenant-group-${tid || 'main'}`}>
                  <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-blue-400" />
                    <div className="text-white font-semibold">
                      {t.is_main ? '★ ' : ''}{t.name}
                      {t.slug ? <span className="text-gray-500 ml-2 text-sm">/{t.slug}</span> : null}
                    </div>
                    <span className="ml-auto text-xs text-gray-500">
                      {byTenant[tid].length} method{byTenant[tid].length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="divide-y divide-white/5">
                    {byTenant[tid].map((m) => (
                      <div key={m.id} className="p-4 flex items-center justify-between gap-3" data-testid={`method-row-${m.id}`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${m.enabled ? 'bg-green-500/15 text-green-400' : 'bg-white/5 text-gray-500'}`}>
                            <DollarSign className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="text-white font-medium">
                              {m.name}
                              {(m.name || '').toLowerCase().includes('chime') && (
                                <span className="ml-2 text-xs bg-purple-500/15 text-purple-300 border border-purple-500/30 rounded-full px-2 py-0.5">
                                  → Chime In Bot
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              <code>{m.method || m.code}</code>
                              {m.flow ? ` · ${m.flow}` : ''}
                              {m.min_amount ? ` · min $${m.min_amount}` : ''}
                              {m.max_amount ? ` · max $${m.max_amount}` : ''}
                              {(m.preset_amounts || m.allowed_amounts || []).length ? ` · set: ${(m.preset_amounts || m.allowed_amounts || []).join(', ')}` : ''}
                            </div>
                            {m.receiving_address ? (
                              <div className="text-xs text-blue-300 mt-1 max-w-lg truncate">Receiving: {m.receiving_address}</div>
                            ) : null}
                            {m.instructions ? (
                              <div className="text-xs text-gray-400 mt-1 max-w-lg truncate">{m.instructions}</div>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" onClick={() => toggle(m)} data-testid={`method-toggle-${m.id}`}>
                            {m.enabled ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                          </Button>
                          <Button variant="outline" onClick={() => { setEditing(m); setShowForm(true); }} data-testid={`method-edit-${m.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" onClick={() => remove(m)} data-testid={`method-delete-${m.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
